/* Translation runtime — zero deps, zero cost, ready for Option C later.

   How it works:
   - All 7 locale JS modules are statically imported so the bundle has every
     language ready at parse time (no network round-trip when switching). Each
     file is ~5 KB, so the full set is ~35 KB — tiny vs an i18n library.
   - `useTranslation()` reads from a React context whose value is driven by
     `localStorage['wasntme.locale']`.
   - Missing keys silently fall back to the English string, so partial
     translation never breaks the UI.
   - Switching language is instant: the context value flips and React re-renders.
   - `<html lang>` is kept in sync so browsers (Chrome/Safari/Edge) can offer
     their native translate for untranslated routes.

   Future Option C hook (on-demand GPT translation):
   - Replace the static import map below with an API that fetches locale JSON
     keyed by (locale, surface). Add a `missingKey` interceptor that queues an
     admin alert; a backend worker populates the missing entries overnight.
*/

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

import en from "./en.json";
import es from "./es.json";
import pt from "./pt.json";
import fr from "./fr.json";
import de from "./de.json";
import ja from "./ja.json";
import zh from "./zh.json";

export const SUPPORTED_LOCALES = [
  { code: "en", label: "English",            native: "English" },
  { code: "es", label: "Spanish",            native: "Español" },
  { code: "pt", label: "Portuguese",         native: "Português" },
  { code: "fr", label: "French",             native: "Français" },
  { code: "de", label: "German",             native: "Deutsch" },
  { code: "ja", label: "Japanese",           native: "日本語" },
  { code: "zh", label: "Chinese (Simplified)", native: "简体中文" },
];

const BUNDLES = { en, es, pt, fr, de, ja, zh };
const STORAGE_KEY = "wasntme.locale";
const DEFAULT_LOCALE = "en";

/** Pick the best locale on first visit: localStorage → browser navigator → en. */
function detectInitialLocale() {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && BUNDLES[stored]) return stored;
  } catch (e) { /* storage unavailable */ }
  const navLang = (navigator.language || "en").toLowerCase();
  // Map browser → our supported set.
  if (navLang.startsWith("es")) return "es";
  if (navLang.startsWith("pt")) return "pt";
  if (navLang.startsWith("fr")) return "fr";
  if (navLang.startsWith("de")) return "de";
  if (navLang.startsWith("ja")) return "ja";
  if (navLang.startsWith("zh")) return "zh";
  return DEFAULT_LOCALE;
}

const I18nContext = createContext({
  locale: DEFAULT_LOCALE,
  bundle: en,
  setLocale: () => {},
});

export const I18nProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(detectInitialLocale);

  // Keep <html lang> and localStorage in sync so browser-native translate
  // (and screen readers) can do the right thing.
  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, locale); } catch (e) { /* no-op */ }
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const setLocale = useCallback((next) => {
    if (BUNDLES[next]) setLocaleState(next);
  }, []);

  const value = useMemo(
    () => ({ locale, bundle: BUNDLES[locale] || en, setLocale }),
    [locale, setLocale],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

/** Look up `common.saved` → bundle.common.saved; fall back to English, then to the key. */
function resolve(bundle, path) {
  const parts = path.split(".");
  let cur = bundle;
  for (const p of parts) {
    if (cur && typeof cur === "object" && p in cur) cur = cur[p];
    else return undefined;
  }
  return typeof cur === "string" ? cur : undefined;
}

export const useTranslation = () => {
  const { locale, bundle, setLocale } = useContext(I18nContext);
  const t = useCallback((path, fallback = "") => {
    const hit = resolve(bundle, path);
    if (hit != null) return hit;
    const enHit = resolve(en, path);
    return enHit != null ? enHit : (fallback || path);
  }, [bundle]);
  return { t, locale, setLocale };
};

/** Build a "Translate this page in Google Translate" URL for the current page.
 *  This is unofficial but still works in 2026; we treat it as a graceful
 *  fallback so users with unsupported languages still have a path. */
export function googleTranslateUrl(targetLocale = "auto") {
  if (typeof window === "undefined") return "#";
  const u = encodeURIComponent(window.location.href);
  return `https://translate.google.com/translate?sl=auto&tl=${targetLocale}&u=${u}`;
}
